import { faBell } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTaskActions } from "@notifications/useTaskActions";
import { type CardSeverity } from "@subjects/cardSeverity";
import { SignTaskCard } from "@tasks/SignTaskCard";
import { TaskCardItem } from "@tasks/TaskCardItem";
import { SIGN, type Task } from "@tasks/tasksClient";
import { useDismissable } from "@utils/use-dismissable";
import { ViewLifecycleBadge } from "@views/ViewLifecycleBadge";
import type { View } from "@views/viewsClient";
import { useViewTasks } from "@views/viewsQueries";
import { useState } from "react";

/**
 * The per-view task bell (WEB-3247): the open tasks pointing at a view plus its sign-off
 * badge, surfaced on the view itself.
 *
 * The opened-view bell fetches the authoritative per-view list (`/views/{id}/tasks`,
 * BE-2034) — one request, always complete. Compact card bells read the `openTaskCount`
 * already on the view payload, so a list of cards costs no extra requests; the full
 * tappable list lives on the opened view.
 */
export const ViewTaskBell = ({
  view,
  compact = false,
}: {
  view: View;
  compact?: boolean;
}) => (compact ? <CompactBell view={view} /> : <FullBell view={view} />);

const CompactBell = ({ view }: { view: View }) => {
  const count = view.openTaskCount;
  if (count === 0 && view.status === "open") return null;

  return (
    <span className="flex items-center gap-1.5">
      <ViewLifecycleBadge status={view.status} />
      {count > 0 && (
        <span
          className="flex items-center gap-1 text-xs font-semibold text-annotation"
          data-testid={`view-task-count-${view.id}`}
        >
          <FontAwesomeIcon icon={faBell} aria-hidden="true" />
          {count}
        </span>
      )}
    </span>
  );
};

const FullBell = ({ view }: { view: View }) => {
  const { data: tasks = [] } = useViewTasks(view.id);
  const [open, setOpen] = useState(false);
  const containerRef = useDismissable(open, () => setOpen(false));

  // The endpoint returns the view's whole task history; the bell only counts
  // what's still actionable, so a signed view's closed sign task doesn't linger.
  const openTasks = tasks.filter((t) => t.status === "open");
  const count = openTasks.length;
  const signed = view.status === "signed";
  // The bell is for acting on open tasks; a signed view shows only its badge,
  // and no bell once nothing is left to act on.
  if (!signed && count === 0) return null;

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center gap-2">
        {signed && <ViewLifecycleBadge status={view.status} />}
        {count > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={`View tasks (${count})`}
            data-testid={`view-task-bell-${view.id}`}
            className="relative flex h-9 w-9 items-center justify-center rounded-full text-annotation transition-colors hover:bg-recess hover:text-ink"
          >
            <FontAwesomeIcon
              icon={faBell}
              className="text-lg"
              aria-hidden="true"
            />
            <span className="absolute right-0 top-0 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-ink-flag px-1 text-[10px] font-bold text-ink-inverse">
              {count}
            </span>
          </button>
        )}
      </div>

      {open && count > 0 && (
        <div
          role="dialog"
          aria-label={`Tasks for ${view.name}`}
          data-testid={`view-task-menu-${view.id}`}
          className="absolute right-0 z-dropdown mt-2 w-[21.25rem] rounded-sheet border border-rule bg-card p-2 text-ink shadow-lift-lg"
        >
          <ViewTaskMenu view={view} tasks={openTasks} />
        </div>
      )}
    </div>
  );
};

const ViewTaskMenu = ({ view, tasks }: { view: View; tasks: Task[] }) => {
  const { handleAction, isAnswered } = useTaskActions();

  // Optimistically drop a task the moment it's resolved here, so its card can't
  // be clicked twice before the feed refetch lands.
  const visible = tasks.filter((task) => !isAnswered(task.id));

  return (
    <>
      <div className="px-2.5 py-2">
        <span className="text-annotation-caps text-annotation">Tasks</span>
      </div>

      {visible.length === 0 ? (
        <p className="px-4 pb-6 pt-4 text-center text-sm text-annotation">
          Nothing open on this view.
        </p>
      ) : (
        <ul className="flex max-h-96 flex-col gap-2 overflow-y-auto p-1">
          {visible.map((task) => (
            <li key={task.id}>
              <ViewTaskRow
                task={task}
                view={view}
                severity={task.severity}
                handleAction={handleAction}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

const ViewTaskRow = ({
  task,
  view,
  severity,
  handleAction,
}: {
  task: Task;
  view: View;
  severity: CardSeverity;
  handleAction: (
    task: Task,
    action: string,
    data: Record<string, unknown>,
  ) => void;
}) => {
  if (task.kind === SIGN) {
    return (
      <SignTaskCard
        task={task}
        view={{ id: view.id, name: view.name, status: view.status }}
        onDecline={() => handleAction(task, "dismiss", {})}
        showOpenView={false}
      />
    );
  }

  return (
    <TaskCardItem task={task} severity={severity} handleAction={handleAction} />
  );
};
